import { useState } from "react";
import { Link } from "react-router-dom";
import type { InsurancePageData } from "@/mocks/insurancePages";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import FloatingCTA from "@/components/feature/FloatingCTA";
import Breadcrumbs from "@/components/feature/Breadcrumbs";
import SEO from "@/components/feature/SEO";
import WhatsAppWidget from "@/components/feature/WhatsAppWidget";
import { faqPageSchema, breadcrumbSchema, webpageSchema, serviceReviewSchema, siteUrl } from "@/lib/schemas";
import { supabase, isSupabaseConnected, supabaseFunctionsBaseUrl, supabaseAnonKeyClean } from "@/lib/supabase";
import { testimonials } from "@/mocks/testimonials";
import { trackEvent } from "@/lib/gtag";

interface ServicePageProps {
  data: InsurancePageData;
}

export default function ServicePage({ data }: ServicePageProps) {
  const [openFaq, setOpenFaq] = useState<number | null>(0);
  const [formData, setFormData] = useState({
    name: "",
    phone: "",
    email: "",
    city: "",
    insurance_type: data.title,
    message: "",
  });
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    setSubmitError("");
    console.log("[Service Page] Submit started for", data.title);

    if (!isSupabaseConnected || !supabase) {
      console.error("[Service Page] Supabase NOT connected");
      setSubmitting(false);
      setSubmitError("Unable to connect to our system right now. Please call us at +91 99993 65655 or WhatsApp us.");
      return;
    }

    console.log("[Service Page] Supabase connected, attempting insert...");
    try {
      const { data: insertData, error } = await supabase.from("leads").insert({
        name: formData.name,
        email: formData.email || null,
        phone: formData.phone,
        city: formData.city || null,
        insurance_type: formData.insurance_type,
        notes: formData.message || null,
        source: `${data.title} Service Page`,
        status: "new",
      }).select();

      console.log("[Service Page] Insert result:", { insertData, error });

      if (error) {
        console.error("[Service Page] Insert error:", error);
        setSubmitting(false);
        setSubmitError(error.message || "Something went wrong saving your request. Please try again or call +91 99993 65655.");
        return;
      }

      console.log("[Service Page] Insert succeeded, data:", insertData);

      // Notify admins via edge function (fire-and-forget)
      try {
        const edgeUrl = `${supabaseFunctionsBaseUrl}/functions/v1/send-lead-notification`;
        await fetch(edgeUrl, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "Authorization": `Bearer ${supabaseAnonKeyClean}`,
          },
          body: JSON.stringify({
            name: formData.name,
            email: formData.email,
            phone: formData.phone,
            city: formData.city,
            insurance_type: formData.insurance_type,
            source: `${data.title} Service Page`,
            notes: formData.message,
          }),
        });
      } catch {
        // Silently ignore notification failures
      }

      setSubmitting(false);
      setSubmitted(true);
      trackEvent("generate_lead", {
        insurance_type: data.title,
        page_location: window.location.href,
        value: 1,
        currency: "INR",
      });
    } catch (err) {
      setSubmitting(false);
      setSubmitError("Network error. Please check your connection and try again, or call +91 99993 65655.");
    }
  };

  const baseUrl = siteUrl;
  const pageUrl = `${baseUrl}/${data.slug}`;

  const pageReviews = testimonials.filter((t) => t.insuranceType === data.title);
  const reviewSchemaInput = pageReviews.length > 0
    ? pageReviews.map((r) => ({ name: r.name, rating: r.rating, reviewBody: r.reviewText, datePublished: r.datePublished }))
    : testimonials.slice(0, 4).map((r) => ({ name: r.name, rating: r.rating, reviewBody: r.reviewText, datePublished: r.datePublished }));

  const schemas = [
    webpageSchema(data.metaTitle, data.metaDescription, pageUrl),
    faqPageSchema(data.faqs),
    serviceReviewSchema(data.title, pageUrl, reviewSchemaInput),
    breadcrumbSchema([
      { name: "Home", url: baseUrl },
      { name: data.title, url: pageUrl },
    ]),
  ];

  return (
    <div className="min-h-screen bg-white">
      <SEO
        title={data.metaTitle}
        description={data.metaDescription}
        keywords={`${data.title}, best ${data.title.toLowerCase()} India, compare ${data.title.toLowerCase()}, insurance advisor, ExpertAdvise.in`}
        canonical={pageUrl}
        ogUrl={pageUrl}
        schemas={schemas}
      />
      <Navbar />
      <main>
        {/* Hero Section */}
        <section className="relative min-h-[520px] md:min-h-[600px] flex items-center overflow-hidden pt-20 md:pt-24">
          <div className="absolute inset-0">
            <div className="absolute inset-0 bg-gradient-to-br from-primary-900 via-slate-900 to-primary-800">
              <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,_var(--tw-gradient-stops))] from-primary-600/20 via-transparent to-transparent"></div>
            </div>
            <div className="absolute inset-0 bg-gradient-to-r from-slate-900/85 via-slate-900/70 to-slate-900/50"></div>
          </div>
          <div className="relative z-10 max-w-7xl mx-auto px-4 md:px-6 py-14 md:py-24 w-full">
            <div className="max-w-3xl">
              <Breadcrumbs
                items={[
                  { name: "Home", href: "/" },
                  { name: data.title },
                ]}
                className="mb-4"
              />
              <span className="inline-block bg-white/15 backdrop-blur-sm text-white/90 text-xs font-semibold uppercase tracking-wider px-3 py-1.5 rounded-full mb-4 border border-white/20">
                {data.title}
              </span>
              <h1 className="text-3xl md:text-4xl lg:text-5xl font-extrabold text-white leading-tight mb-4">
                {data.headline}
              </h1>
              <p className="text-base md:text-lg text-white/80 leading-relaxed mb-6 max-w-2xl">
                {data.subheadline}
              </p>
              <div className="flex flex-wrap gap-3">
                <Link
                  to={`/book-appointment?source=${encodeURIComponent(data.title + " Service Page")}`}
                  className="bg-cta-600 hover:bg-cta-700 text-white font-bold text-sm px-6 py-3 rounded-full shadow-lg transition-all hover:-translate-y-0.5 whitespace-nowrap flex items-center gap-2"
                >
                  <i className="ri-calendar-check-line"></i>
                  Book Free Consultation
                </Link>
                {data.calculatorRoute && (
                  <Link
                    to={data.calculatorRoute}
                    className="bg-white/90 hover:bg-white text-slate-900 font-bold text-sm px-6 py-3 rounded-full shadow-lg transition-all hover:-translate-y-0.5 whitespace-nowrap flex items-center gap-2"
                  >
                    <i className="ri-calculator-line"></i>
                    Use Calculator
                  </Link>
                )}
                <Link
                  to="/compare-plans"
                  className="bg-primary-600/90 hover:bg-primary-600 text-white font-bold text-sm px-6 py-3 rounded-full shadow-lg transition-all hover:-translate-y-0.5 whitespace-nowrap flex items-center gap-2"
                >
                  <i className="ri-arrow-left-right-line"></i>
                  {data.ctaText}
                </Link>
              </div>
            </div>
          </div>
        </section>

        {/* Key Features */}
        <section className="py-16 md:py-20 bg-slate-50">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="text-center max-w-3xl mx-auto mb-10 md:mb-14">
              <span className="inline-block bg-primary-50 text-primary-700 text-xs font-semibold uppercase tracking-wider px-3 py-1 rounded-full mb-3">
                Key Features
              </span>
              <h2 className="text-2xl md:text-3xl font-bold text-slate-900 mb-3">
                Why {data.title} Matters
              </h2>
              <p className="text-slate-600 text-base md:text-lg">
                Comprehensive coverage tailored to your specific needs and lifestyle.
              </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
              {data.keyFeatures?.map((feature, idx) => (
                <div key={idx} className="bg-white rounded-2xl p-6 border border-slate-100 hover:shadow-lg hover:border-primary-200 transition-all duration-300 group">
                  <div className="w-12 h-12 rounded-xl bg-primary-50 text-primary-600 flex items-center justify-center mb-4 group-hover:bg-primary-600 group-hover:text-white transition-all duration-300">
                    <i className={`${feature.icon} text-2xl`}></i>
                  </div>
                  <h3 className="text-base font-bold text-slate-900 mb-2">{feature.title}</h3>
                  <p className="text-sm text-slate-500 leading-relaxed">{feature.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Comparison Table */}
        <section className="py-16 md:py-20 bg-white">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="text-center max-w-3xl mx-auto mb-10 md:mb-14">
              <span className="inline-block bg-amber-50 text-amber-700 text-xs font-semibold uppercase tracking-wider px-3 py-1 rounded-full mb-3">
                Plan Comparison
              </span>
              <h2 className="text-2xl md:text-3xl font-bold text-slate-900 mb-3">
                Compare {data.title} Plans Side-by-Side
              </h2>
              <p className="text-slate-600 text-base md:text-lg">
                Transparent comparison to help you make the right choice.
              </p>
            </div>
            <div className="overflow-x-auto rounded-2xl border border-slate-200 shadow-sm -mx-4 md:mx-0">
              <table className="w-full text-sm min-w-[640px]">
                <thead>
                  <tr className="bg-slate-900">
                    {data.comparisonHeaders?.map((header, idx) => (
                      <th
                        key={idx}
                        className={`text-left font-semibold text-white px-4 py-3.5 whitespace-nowrap ${
                          idx === 0 ? "sticky left-0 bg-slate-900 z-10" : ""
                        }`}
                      >
                        {header}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {data.comparisonRows?.map((row, ridx) => (
                    <tr
                      key={ridx}
                      className={`border-b border-slate-100 ${
                        ridx % 2 === 0 ? "bg-white" : "bg-slate-50/50"
                      } hover:bg-primary-50/30 transition-colors`}
                    >
                      {row.values.map((val, vidx) => (
                        <td
                          key={vidx}
                          className={`px-4 py-3.5 whitespace-nowrap ${
                            vidx === 0
                              ? "font-semibold text-slate-800 sticky left-0 bg-inherit z-10"
                              : "text-slate-600"
                          }`}
                        >
                          {val}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="text-center mt-8">
              <Link
                to="/compare-plans"
                className="inline-flex items-center gap-2 bg-slate-900 hover:bg-slate-800 text-white font-semibold text-sm px-6 py-3 rounded-full transition-colors"
              >
                <i className="ri-arrow-left-right-line"></i>
                {data.ctaText}
              </Link>
            </div>
          </div>
        </section>

        {/* Browse Insurer Brochures CTA — Health Insurance Only */}
        {data.slug === "health-insurance" && (
          <section className="py-12 md:py-16 bg-gradient-to-br from-teal-50 to-primary-50 border-y border-teal-100">
            <div className="max-w-5xl mx-auto px-4 md:px-6 text-center">
              <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-teal-600 to-primary-600 flex items-center justify-center mx-auto mb-5 shadow-lg">
                <i className="ri-file-list-3-line text-white text-2xl"></i>
              </div>
              <h2 className="text-2xl md:text-3xl font-bold text-slate-900 mb-3">
                Browse Official Insurer Brochures & Plans
              </h2>
              <p className="text-slate-600 max-w-2xl mx-auto mb-6 text-sm md:text-base leading-relaxed">
                Download IRDAI-approved plan brochures from HDFC ERGO, Niva Bupa, and ICICI Lombard. 
                Compare all plans side-by-side, read policy details, and make an informed decision 
                with our complete comparison guide.
              </p>
              <div className="flex flex-wrap items-center justify-center gap-3">
                <Link
                  to="/health-insurance-companies"
                  className="inline-flex items-center gap-2 bg-cta-600 hover:bg-cta-700 text-white font-bold text-sm md:text-base px-6 py-3 rounded-full transition-all shadow-lg shadow-cta-600/30 hover:shadow-xl whitespace-nowrap"
                >
                  <i className="ri-hospital-line text-lg"></i>
                  Browse Insurer Brochures & Plans
                </Link>
                <Link
                  to="/health-insurance-companies"
                  className="inline-flex items-center gap-2 bg-white hover:bg-slate-50 text-slate-800 font-semibold text-sm px-6 py-3 rounded-full transition-all border border-slate-200 shadow-sm hover:shadow whitespace-nowrap"
                >
                  <i className="ri-download-line"></i>
                  Download Brochures
                </Link>
              </div>
              <div className="mt-6 flex items-center justify-center gap-6 text-xs text-slate-500">
                <span className="flex items-center gap-1.5">
                  <i className="ri-check-line text-cta-500"></i>
                  3 Top Insurers
                </span>
                <span className="flex items-center gap-1.5">
                  <i className="ri-check-line text-cta-500"></i>
                  12 Plans Compared
                </span>
                <span className="flex items-center gap-1.5">
                  <i className="ri-check-line text-cta-500"></i>
                  Official PDFs
                </span>
              </div>
            </div>
          </section>
        )}

        {/* Benefits */}
        <section className="py-16 md:py-20 bg-gradient-to-br from-primary-50 to-blue-50">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="text-center max-w-3xl mx-auto mb-10 md:mb-14">
              <span className="inline-block bg-white text-primary-700 text-xs font-semibold uppercase tracking-wider px-3 py-1 rounded-full mb-3 shadow-sm">
                Benefits
              </span>
              <h2 className="text-2xl md:text-3xl font-bold text-slate-900 mb-3">
                Why Choose {data.title} Through ExpertAdvise?
              </h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
              {data.benefits?.map((benefit, idx) => (
                <div key={idx} className="bg-white rounded-2xl p-6 shadow-sm border border-white/50 hover:shadow-md transition-all">
                  <div className="flex items-center gap-2 mb-3">
                    <span className="w-6 h-6 rounded-full bg-cta-100 text-cta-600 flex items-center justify-center shrink-0">
                      <i className="ri-check-line text-xs font-bold"></i>
                    </span>
                    <h3 className="text-base font-bold text-slate-900">{benefit.title}</h3>
                  </div>
                  <p className="text-sm text-slate-500 leading-relaxed">{benefit.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Exclusions */}
        <section className="py-16 md:py-20 bg-white">
          <div className="max-w-4xl mx-auto px-4 md:px-6">
            <div className="text-center max-w-3xl mx-auto mb-10 md:mb-14">
              <span className="inline-block bg-red-50 text-red-700 text-xs font-semibold uppercase tracking-wider px-3 py-1 rounded-full mb-3">
                Important to Know
              </span>
              <h2 className="text-2xl md:text-3xl font-bold text-slate-900 mb-3">
                What&apos;s Not Covered
              </h2>
              <p className="text-slate-600 text-base md:text-lg">
                Transparency is our policy. Here are common exclusions you should be aware of.
              </p>
            </div>
            <div className="bg-slate-50 rounded-2xl p-6 md:p-8 border border-slate-100">
              <ul className="space-y-4">
                {data.exclusions?.map((exclusion, idx) => (
                  <li key={idx} className="flex items-start gap-3">
                    <span className="w-6 h-6 rounded-full bg-red-100 text-red-500 flex items-center justify-center shrink-0 mt-0.5">
                      <i className="ri-close-line text-xs font-bold"></i>
                    </span>
                    <p className="text-sm md:text-base text-slate-600">{exclusion}</p>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className="py-16 md:py-20 bg-slate-50">
          <div className="max-w-4xl mx-auto px-4 md:px-6">
            <div className="text-center max-w-3xl mx-auto mb-10 md:mb-14">
              <span className="inline-block bg-amber-50 text-amber-700 text-xs font-semibold uppercase tracking-wider px-3 py-1 rounded-full mb-3">
                Frequently Asked Questions
              </span>
              <h2 className="text-2xl md:text-3xl font-bold text-slate-900 mb-3">
                Got Questions? We&apos;ve Got Answers
              </h2>
            </div>
            <div className="space-y-3">
              {data.faqs?.map((faq, idx) => (
                <div
                  key={idx}
                  className={`bg-white rounded-xl border transition-all ${
                    openFaq === idx ? "border-primary-300 shadow-md" : "border-slate-100"
                  }`}
                >
                  <button
                    className="w-full flex items-center justify-between gap-4 px-5 py-4 text-left cursor-pointer"
                    onClick={() => setOpenFaq(openFaq === idx ? null : idx)}
                    aria-expanded={openFaq === idx}
                  >
                    <span className="text-sm md:text-base font-semibold text-slate-800">
                      {faq.question}
                    </span>
                    <i
                      className={`ri-arrow-down-s-line text-slate-400 text-lg shrink-0 transition-transform ${
                        openFaq === idx ? "rotate-180 text-primary-500" : ""
                      }`}
                    ></i>
                  </button>
                  {openFaq === idx && (
                    <div className="px-5 pb-5">
                      <p className="text-sm md:text-base text-slate-600 leading-relaxed">{faq.answer}</p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Lead Form Section */}
        <section className="py-16 md:py-20 bg-gradient-to-br from-primary-900 to-primary-800">
          <div className="max-w-5xl mx-auto px-4 md:px-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 md:gap-16 items-start">
              {/* Left: Info */}
              <div className="text-white">
                <h2 className="text-2xl md:text-3xl font-bold mb-4">{data.leadFormTitle}</h2>
                <p className="text-white/80 text-base md:text-lg leading-relaxed mb-6">
                  {data.leadFormSubtitle}
                </p>
                <div className="space-y-4 mb-8">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-white/15 flex items-center justify-center">
                      <i className="ri-check-line text-white"></i>
                    </div>
                    <span className="text-white/90 text-sm">Free, no-obligation consultation</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-white/15 flex items-center justify-center">
                      <i className="ri-check-line text-white"></i>
                    </div>
                    <span className="text-white/90 text-sm">Compare plans from 20+ insurers</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-white/15 flex items-center justify-center">
                      <i className="ri-check-line text-white"></i>
                    </div>
                    <span className="text-white/90 text-sm">Lifetime claims support included</span>
                  </div>
                </div>
                <div className="flex items-center gap-3 bg-white/10 rounded-xl p-4">
                  <div className="w-12 h-12 rounded-full bg-cta-500 flex items-center justify-center shrink-0">
                    <i className="ri-phone-line text-white text-lg"></i>
                  </div>
                  <div>
                    <p className="text-xs text-white/60">Prefer to talk?</p>
                    <a href="tel:+919999365655" className="text-lg font-bold text-white hover:text-cta-300 transition-colors">
                      +91 99993 65655
                    </a>
                  </div>
                </div>
              </div>

              {/* Right: Form */}
              <div className="bg-white rounded-2xl p-6 md:p-8 shadow-xl">
                {submitted ? (
                  <div className="text-center py-10">
                    <div className="w-16 h-16 rounded-full bg-cta-100 text-cta-600 flex items-center justify-center mx-auto mb-4">
                      <i className="ri-check-double-line text-3xl"></i>
                    </div>
                    <h3 className="text-xl font-bold text-slate-900 mb-2">Thank You!</h3>
                    <p className="text-slate-500 text-sm mb-4">
                      Our expert advisor will contact you within 15 minutes with personalized recommendations.
                    </p>
                    <div className="flex flex-col sm:flex-row items-center justify-center gap-3 mt-4">
                      <WhatsAppWidget
                        variant="success"
                        userPhone={formData.phone}
                        insuranceType={data.title}
                      >
                        Chat on WhatsApp
                      </WhatsAppWidget>
                      <button
                        onClick={() => {
                          setSubmitted(false);
                          setFormData({ name: "", phone: "", email: "", city: "", insurance_type: data.title, message: "" });
                        }}
                        className="text-sm text-primary-600 hover:text-primary-700 font-medium cursor-pointer"
                      >
                        Submit another request
                      </button>
                    </div>
                  </div>
                ) : (
                  <form onSubmit={handleSubmit} className="space-y-4">
                    {submitError && (
                      <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-lg px-4 py-3">
                        {submitError}
                      </div>
                    )}
                    <div>
                      <label className="block text-xs font-semibold text-slate-700 mb-1.5">Full Name *</label>
                      <input
                        type="text"
                        required
                        name="name"
                        placeholder="Enter your full name"
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        className="w-full px-4 py-3 rounded-lg border border-slate-200 text-sm focus:outline-none focus:border-primary-500 focus:ring-2 focus:ring-primary-100 transition-all"
                      />
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-xs font-semibold text-slate-700 mb-1.5">Mobile Number *</label>
                        <input
                          type="tel"
                          required
                          name="phone"
                          placeholder="+91 98765 43210"
                          value={formData.phone}
                          onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                          className="w-full px-4 py-3 rounded-lg border border-slate-200 text-sm focus:outline-none focus:border-primary-500 focus:ring-2 focus:ring-primary-100 transition-all"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-semibold text-slate-700 mb-1.5">Email Address *</label>
                        <input
                          type="email"
                          required
                          name="email"
                          placeholder="you@email.com"
                          value={formData.email}
                          onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                          className="w-full px-4 py-3 rounded-lg border border-slate-200 text-sm focus:outline-none focus:border-primary-500 focus:ring-2 focus:ring-primary-100 transition-all"
                        />
                      </div>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-xs font-semibold text-slate-700 mb-1.5">City *</label>
                        <input
                          type="text"
                          required
                          name="city"
                          placeholder="e.g. Delhi"
                          value={formData.city}
                          onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                          className="w-full px-4 py-3 rounded-lg border border-slate-200 text-sm focus:outline-none focus:border-primary-500 focus:ring-2 focus:ring-primary-100 transition-all"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-semibold text-slate-700 mb-1.5">Insurance Type</label>
                        <input
                          type="text"
                          readOnly
                          name="insurance_type"
                          value={formData.insurance_type}
                          className="w-full px-4 py-3 rounded-lg border border-slate-200 bg-slate-50 text-sm text-slate-500 cursor-not-allowed"
                        />
                      </div>
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-slate-700 mb-1.5">Additional Requirements</label>
                      <textarea
                        name="message"
                        rows={3}
                        maxLength={500}
                        placeholder="Tell us about your specific needs, budget, or any questions..."
                        value={formData.message}
                        onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                        className="w-full px-4 py-3 rounded-lg border border-slate-200 text-sm focus:outline-none focus:border-primary-500 focus:ring-2 focus:ring-primary-100 transition-all resize-none"
                      />
                    </div>
                    <button
                      type="submit"
                      disabled={submitting}
                      className="w-full bg-cta-600 hover:bg-cta-700 disabled:opacity-60 disabled:cursor-not-allowed text-white font-bold text-sm py-3.5 rounded-lg transition-colors shadow-md flex items-center justify-center gap-2 cursor-pointer"
                    >
                      {submitting ? (
                        <div className="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin" />
                      ) : (
                        <>Get Free Quote & Consultation</>
                      )}
                    </button>
                    <p className="text-[11px] text-slate-400 text-center">
                      By submitting, you agree to our{" "}
                      <Link to="/privacy-policy" className="underline hover:text-slate-600">Privacy Policy</Link>{" "}
                      and{" "}
                      <Link to="/terms" className="underline hover:text-slate-600">Terms of Service</Link>
                    </p>
                  </form>
                )}
              </div>
            </div>
          </div>
        </section>

        {/* Related Services */}
        <section className="py-16 md:py-20 bg-white">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <h2 className="text-xl md:text-2xl font-bold text-slate-900 text-center mb-8 md:mb-12">
              You May Also Be Interested In
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
              {data.relatedServices?.map((service, idx) => (
                <Link
                  key={idx}
                  to={service.route}
                  className="group bg-slate-50 hover:bg-white rounded-xl p-5 md:p-6 border border-slate-100 hover:border-primary-200 hover:shadow-md transition-all text-center"
                >
                  <div className="w-12 h-12 rounded-xl bg-primary-50 text-primary-600 flex items-center justify-center mx-auto mb-3 group-hover:bg-primary-600 group-hover:text-white transition-all"
                  >
                    <i className={`${service.icon} text-xl`}></i>
                  </div>
                  <h3 className="text-sm font-bold text-slate-800 group-hover:text-primary-700 transition-colors">
                    {service.title}
                  </h3>
                </Link>
              ))}
            </div>
          </div>
        </section>
      </main>
      <Footer />
      <FloatingCTA />
    </div>
  );
}